import { CIMSModule } from 'shared'
import { CreateBatchReportRequestInput } from 'schema'

enum LocalStorageKey {
  ProjectId = 'projectId',
  VisiblePayItemConnectionGroups = 'visiblePayItemConnectionGroups',
  CreateBatchReportRequestInput = 'createBatchReportRequestInput',
  PageId = 'pageId',
}

/** @see {@link cimsStorage} */
class CIMSStorage {
  /** initializes a new instance of {@link CIMSStorage} */
  constructor() {
    // remove/save old local storage values
    const projectIdKey = 'projectGlobalId'
    const projectId = localStorage.getItem(projectIdKey)
    if (projectId) localStorage.setItem(LocalStorageKey.ProjectId, projectId)
    localStorage.removeItem(projectIdKey)

    const groupsKey = 'showGroups'
    const groups = localStorage.getItem(groupsKey)
    if (groups)
      localStorage.setItem(
        LocalStorageKey.VisiblePayItemConnectionGroups,
        groups
      )
    localStorage.removeItem(groupsKey)

    localStorage.removeItem('sharePointSiteUrl')
    localStorage.removeItem('pathname')
    localStorage.removeItem('search')

    const argsKey = 'batchReportArgs'
    const args = localStorage.getItem(argsKey)
    if (args)
      localStorage.setItem(LocalStorageKey.CreateBatchReportRequestInput, args)
    localStorage.removeItem(argsKey)

    const navKeyKey = 'navKey'
    const key = localStorage.getItem(navKeyKey)
    if (key) localStorage.setItem(LocalStorageKey.PageId, key)
    localStorage.removeItem(navKeyKey)

    const inputKey = 'createBatchReportRequestInput'
    const input = localStorage.getItem(inputKey)
    if (input)
      localStorage.setItem(LocalStorageKey.CreateBatchReportRequestInput, input)
    localStorage.removeItem(inputKey)
  }

  /**
   * pulls the value associated with the key from local storage and normalizes
   * it into an optional string
   *
   * @param key
   */
  private get(key: LocalStorageKey) {
    const value = localStorage.getItem(key) ?? 'undefined'
    if (['undefined', 'null'].includes(value)) return undefined
    return value
  }
  /**
   * sets the value associated with the key in local storage, returning this
   * for chaining
   *
   * @param key
   * @param value
   */
  private set(key: LocalStorageKey, value: string | undefined) {
    localStorage.setItem(key, value ?? 'undefined')
    return this
  }

  /** gets the current project global id from localStorage */
  get projectId() {
    const projectId = this.get(LocalStorageKey.ProjectId)
    return projectId
  }
  /**
   * __THIS SHOULD ONLY BE CALLED FROM WITHIN GlobalStateContext!__
   *
   * sets a new project global id in local storage
   */
  setProjectId(projectId: string | undefined) {
    return this.set(LocalStorageKey.ProjectId, projectId)
  }

  /**
   * gets the current map of visible custom pay item connection groups from
   * local storage
   */
  get visiblePayItemConnectionGroups() {
    let visible = new Map<string, boolean>()
    try {
      visible = new Map(
        /**
         * i'm ok with asserting this because we're catching errors
         */
        JSON.parse(this.get(LocalStorageKey.VisiblePayItemConnectionGroups)!)
      )
    } catch (err) {
      this.setVisiblePayItemConnectionGroups(visible)
    } finally {
      return visible
    }
  }
  /**
   * __THIS SHOULD ONLY BE CALLED FROM WITHIN GlobalStateContext!__
   *
   * sets a new map for visible custom pay item connection groups in local
   * storage
   */
  setVisiblePayItemConnectionGroups(map: Map<string, boolean> | undefined) {
    return this.set(
      LocalStorageKey.VisiblePayItemConnectionGroups,
      JSON.stringify(Array.from((map ?? new Map()).entries()))
    )
  }

  /**
   * gets the current {@link CreateBatchReportRequestInput} object from local
   * storage
   */
  get createBatchReportRequestInput() {
    let input: Partial<CreateBatchReportRequestInput> = {}
    try {
      input = JSON.parse(
        /**
         * once again, i'm ok with this assertion because we're catching errors
         */
        this.get(LocalStorageKey.CreateBatchReportRequestInput)!
      )
    } catch (err) {
      this.setCreateBatchReportRequestInput(input)
    } finally {
      return input
    }
  }
  /**
   * __THIS SHOULD ONLY BE CALLED FROM WITHIN GlobalStateContext!__
   *
   * sets a new {@link CreateBatchReportRequestInput} object in local storage
   */
  setCreateBatchReportRequestInput(
    input: Partial<CreateBatchReportRequestInput> | undefined
  ) {
    return this.set(
      LocalStorageKey.CreateBatchReportRequestInput,
      JSON.stringify(input ?? {})
    )
  }

  /** gets the current CIMSPageItem id or CIMSModuleItem from local storage */
  get pageId() {
    let pageId = this.get(LocalStorageKey.PageId)
    if (pageId) return pageId
    pageId = CIMSModule.Home.id
    this.setPageId(pageId)
    return pageId
  }
  /**
   * __THIS SHOULD ONLY BE CALLED FROM WITHIN GlobalStateContext!__
   *
   *  sets a new CIMSPageItem or CIMSModuleItem id in local storage
   */
  setPageId(pageId: string | undefined) {
    return this.set(LocalStorageKey.PageId, pageId)
  }
}

/**
 * handles all the {@link localStorage} needs associated with the application
 *
 * You shouldn't ever directly use localStorage, opting to use the getters/setters
 * on this instance instead
 *
 * if you need more localStorage fields, add them to {@link CIMSStorage}
 */
export const cimsStorage = new CIMSStorage()
